import { IconLoaderService } from '@app/core/services/icon-loader.service';
//Based on httpImgSrc pipe but returns SafeResourceUrl for use with <object>, <iframe>, etc.

import { HttpClient, HttpResponse } from "@angular/common/http";
import { ChangeDetectorRef, inject, OnDestroy, Pipe, PipeTransform } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { BehaviorSubject, catchError, distinctUntilChanged, filter, map, Subscription, switchMap, take, tap } from "rxjs";
import { environment } from "@env/environment";


@Pipe({
  name: 'httpResourceSrc',
  standalone: true,
  pure: false,
})
export class HttpResourceSrcPipe implements PipeTransform, OnDestroy {
  private readonly httpClient = inject(HttpClient);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly cdr = inject(ChangeDetectorRef);

  private subscription = new Subscription();
  private latestValue!: SafeResourceUrl;
  private latestObjectUrl: string | null = null;
  private pendingObjectUrl: string | null = null;
  private transformValue = new BehaviorSubject<string>('');
  private loadingResourcePath!: SafeResourceUrl;
  private errorResourcePath!: SafeResourceUrl;
  private resourceUrlTemplate: string = `${environment.apiUrl}/medias/{fileId}/download`;


  constructor() {
    try {
      const loaderSizePx = 64;
      const svgLoaderString = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${loaderSizePx}" height="${loaderSizePx}" viewBox="0 0 50 50">
          <rect width="50" height="50" fill="#ffffff" />
          <circle cx="25" cy="25" r="10" fill="none" stroke="rgba(212, 222, 230, 0.25)" stroke-width="2" />
          <path d="M25 15a10 10 0 0 1 10 10" fill="none" stroke="rgba(48, 165, 249, 0.64)" stroke-width="2" stroke-linecap="round">
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="0.9s"
              repeatCount="indefinite"
            />
          </path>
        </svg>`;
      this.loadingResourcePath = this.domSanitizer.bypassSecurityTrustResourceUrl(`data:image/svg+xml;base64,${btoa(svgLoaderString)}`);
      this.errorResourcePath = this.domSanitizer.bypassSecurityTrustResourceUrl('about:blank');
      
      const transformSubscription = this.transformValue
        .asObservable()
        .pipe(
          filter((v: string | null): v is string => !!v),
          distinctUntilChanged(),
          switchMap((resourcePath: string) =>
            this.httpClient.get(resourcePath, { observe: 'response', responseType: 'blob' }).pipe(
              take(1),
              map((response: HttpResponse<Blob>) => {
                const objectUrl = response.body !== null ? URL.createObjectURL(response.body) : '';
                this.pendingObjectUrl = objectUrl || null;
                return objectUrl;
              }),
              map((unsafeBlobUrl: string) => this.domSanitizer.bypassSecurityTrustResourceUrl(unsafeBlobUrl)),
              filter((blobUrl: SafeResourceUrl) => blobUrl !== this.latestValue),
              catchError((err: any) => {
                console.error('Error loading resource:', err);
                return [this.errorResourcePath];
              }),
            ),
          ),
          tap((resourcePath: SafeResourceUrl) => {
            this.revokeLatestObjectUrl();
            this.latestObjectUrl = this.pendingObjectUrl;
            this.pendingObjectUrl = null;
            this.latestValue = resourcePath;
            this.cdr.markForCheck();
          }),
        )
        .subscribe();
      this.subscription.add(transformSubscription);
    } catch (error) {
      console.error('Error in HttpResourceSrcPipe constructor:', error);
    }
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.revokeLatestObjectUrl();
  }

  public transform(resourcePath: string): SafeResourceUrl {
    if (!resourcePath) {
      return this.errorResourcePath;
    }

    if (resourcePath.startsWith('blob:') || resourcePath.startsWith('data:')) {
      return this.domSanitizer.bypassSecurityTrustResourceUrl(resourcePath);
    }

    const resourceUrl = resourcePath.length === 36 && resourcePath.indexOf('/') < 0 
      ? this.resourceUrlTemplate.replace('{fileId}', resourcePath) 
      : resourcePath;
    this.transformValue.next(resourceUrl);
    return this.latestValue || this.loadingResourcePath;
  }

  private revokeLatestObjectUrl(): void {
    if (this.latestObjectUrl) {
      URL.revokeObjectURL(this.latestObjectUrl);
      this.latestObjectUrl = null;
    }
  }
}
